import React, { useRef, useEffect } from "react";
import SidePanel from "../layout/SidePanel"; 
import CollapsibleCard from "../common/CollapsibleCard"; 
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";

function orderRouteStops(stops) {
  if (!Array.isArray(stops) || stops.length === 0) return [];
  if (Array.isArray(stops[0]) && typeof stops[0][0] === 'number') {
      const coords = [...stops];
      const first = coords[0];
      const last = coords[coords.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
          coords.push([...first]);
      }
      return coords;
  }
  const orderedCoords = [];
  const visitedIds = new Set(); 
  let current = stops.find(s => s.rstIsMainStop === true && !s.previousStop);
  if (!current) current = stops.find(s => !s.previousStop); 
  if (!current) current = stops[0]; 

  while (current && !visitedIds.has(current.rstId || current.stop?.stoId)) {
      if (current.stop) orderedCoords.push([current.stop.stoLongitude, current.stop.stoLatitude]);
      visitedIds.add(current.rstId || current.stop?.stoId);
      if (current.rstIsMainStop === true && !current.nextStop) break; 
      if (current.nextStop?.stoId) {
          const nextId = current.nextStop.stoId;
          let nextRs = stops.find(rs => rs.stop?.stoId === nextId && (!rs.previousStop || rs.nextStop));
          if (!nextRs) nextRs = stops.find(rs => rs.stop?.stoId === nextId);
          current = nextRs;
      } else break;
  }
  if (current && current.previousStop?.stoId) {
      let prevId = current.previousStop.stoId;
      let currentVuelta = stops.find(rs => rs.stop?.stoId === prevId && !rs.nextStop);
      if (!currentVuelta) currentVuelta = stops.find(rs => rs.stop?.stoId === prevId);

      while (currentVuelta && !visitedIds.has(currentVuelta.rstId || currentVuelta.stop?.stoId)) {
          if (currentVuelta.stop) orderedCoords.push([currentVuelta.stop.stoLongitude, currentVuelta.stop.stoLatitude]);
          visitedIds.add(currentVuelta.rstId || currentVuelta.stop?.stoId);
          if (currentVuelta.rstIsMainStop === true && !currentVuelta.previousStop) break; 
          if (currentVuelta.previousStop?.stoId) {
              const nextId = currentVuelta.previousStop.stoId;
              let nextRs = stops.find(rs => rs.stop?.stoId === nextId && !rs.nextStop);
              if (!nextRs) nextRs = stops.find(rs => rs.stop?.stoId === nextId);
              currentVuelta = nextRs;
          } else break;
      }
  }
  if (orderedCoords.length > 0) {
      const first = orderedCoords[0];
      const last = orderedCoords[orderedCoords.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) orderedCoords.push([...first]);
  }
  return orderedCoords;
}

function ComparisonMapView({ data, showOld, showNew }) {
  const mapDiv = useRef(null);
  const viewRef = useRef(null);
  const oldLayerRef = useRef(null);
  const newLayerRef = useRef(null);

  useEffect(() => {
    if (!mapDiv.current || !data) return;
    const map = new Map({ basemap: "streets-navigation-vector" });
    const view = new MapView({
      container: mapDiv.current, map: map,
      center: data.pointsNew && data.pointsNew.length > 0 ? data.pointsNew[0] : [-66.9036, 10.4806],
      zoom: 14, ui: { components: ["zoom"] }
    });
    viewRef.current = view;

    const oldLayer = new GraphicsLayer({ visible: showOld });
    const newLayer = new GraphicsLayer({ visible: showNew });
    oldLayerRef.current = oldLayer;
    newLayerRef.current = newLayer;
    map.addMany([oldLayer, newLayer]);

    const orderedOld = orderRouteStops(data.pointsOld || []);
    const orderedNew = orderRouteStops(data.pointsNew || []);

    if (orderedOld.length > 0) {
        for (let i = 0; i < orderedOld.length - 1; i++) {
            const currentPoint = orderedOld[i];
            const nextPoint = orderedOld[i+1];
            if (currentPoint[0] === nextPoint[0] && currentPoint[1] === nextPoint[1]) continue;
            const pathSegment = [currentPoint, nextPoint];
            const oldPolyline = new Polyline({ paths: [pathSegment], spatialReference: { wkid: 4326 } });
            const oldGraphic = new Graphic({ geometry: oldPolyline, symbol: { type: "simple-line", color: [239, 68, 68, 0.8], width: 3, style: "solid", marker: { style: "arrow", placement: "end" } } });
            oldLayer.add(oldGraphic);
        }
        const uniqueOldStops = orderedOld.slice(0, orderedOld.length - 1);
        uniqueOldStops.forEach(pt => {
           oldLayer.add(new Graphic({ geometry: new Point({ longitude: pt[0], latitude: pt[1] }), symbol: { type: "simple-marker", color: "#EF4444", size: "8px", outline: { width: 1, color: "white" } } }));
        });
    }

    if (orderedNew.length > 0) {
        for (let i = 0; i < orderedNew.length - 1; i++) {
            const currentPoint = orderedNew[i];
            const nextPoint = orderedNew[i+1];
            if (currentPoint[0] === nextPoint[0] && currentPoint[1] === nextPoint[1]) continue;
            const pathSegment = [currentPoint, nextPoint];
            const newPolyline = new Polyline({ paths: [pathSegment], spatialReference: { wkid: 4326 } });
            const newGraphic = new Graphic({ geometry: newPolyline, symbol: { type: "simple-line", color: "#3B82F6", width: 3, style: "solid", marker: { style: "arrow", placement: "end" } } });
            newLayer.add(newGraphic);
        }
        const uniqueNewStops = orderedNew.slice(0, orderedNew.length - 1);
        uniqueNewStops.forEach(pt => {
           newLayer.add(new Graphic({ geometry: new Point({ longitude: pt[0], latitude: pt[1] }), symbol: { type: "simple-marker", color: "#3B82F6", size: "10px", outline: { width: 2, color: "white" } } }));
        });
        const fullExtentPolyline = new Polyline({ paths: [orderedNew], spatialReference: { wkid: 4326 } });
        view.when(() => { view.goTo(fullExtentPolyline.extent.expand(1.5), { duration: 800 }).catch(e => { if (e.name !== "AbortError") console.warn(e); }); });
    }
    return () => { if (viewRef.current) viewRef.current.destroy(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]); 

  useEffect(() => {
      if (oldLayerRef.current) oldLayerRef.current.visible = showOld;
      if (newLayerRef.current) newLayerRef.current.visible = showNew;
  }, [showOld, showNew]);

  return (
      <>
          <div ref={mapDiv} style={{ width: '100%', height: '100%' }} />
          <div style={{ position: 'absolute', bottom: 16, right: 16, background: 'rgba(255,255,255,0.95)', padding: '10px 14px', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', zIndex: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', opacity: showOld ? 1 : 0.4, transition: 'opacity 0.2s' }}>
                  <div style={{ width: '20px', height: '4px', background: '#EF4444' }}></div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: '#EF4444' }}>Ayer (Paradas / Ruta)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', opacity: showNew ? 1 : 0.4, transition: 'opacity 0.2s' }}>
                  <div style={{ width: '20px', height: '4px', background: '#3B82F6' }}></div>
                  <span style={{ fontSize: '11px', fontWeight: 800, color: '#3B82F6' }}>Hoy (Paradas / Ruta)</span>
              </div>
          </div>
      </>
  );
}

export default function GenerationTab({ 
  isLoading, 
  generationData, 
  groupedGenerationData, 
  activeCityCards, 
  toggleCityCard, 
  selectedGenRoute, 
  setSelectedGenRoute, 
  mapUpdateKey, 
  showOldRoute, 
  setShowOldRoute, 
  showNewRoute, 
  setShowNewRoute, 
  handleOpenRevertModal 
}) {
  return (
    <div className="content-grid" style={{ height: 'calc(100vh - 120px)' }}>
        <SidePanel title="Ciudades modificadas">
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: '30px 10px', color: '#6B7280', fontSize: '13px' }}>Cargando reportes...</div>
            ) : generationData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px 10px', color: '#9CA3AF', fontSize: '13px' }}>No hay rutas modificadas.</div>
            ) : (
                Object.entries(groupedGenerationData).map(([cityName, routes]) => (
                    <CollapsibleCard key={cityName} title={cityName} subtitle={`${routes.length} rutas modificadas`} isActive={activeCityCards.has(cityName)} onToggleActive={() => toggleCityCard(cityName)} showToggleBtn={false}>
                        {routes.map((item) => {
                            const isSelected = selectedGenRoute?.id === item.id;
                            return (
                                <div key={item.id} onClick={() => setSelectedGenRoute(item)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px dashed #E5E7EB', cursor: 'pointer' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '13px', fontWeight: 500, color: isSelected ? '#1F2937' : '#6B7280' }}>
                                            {item.routeName} <span style={{ fontSize: '10px', color: '#9CA3AF', marginLeft: '4px' }}>(ID: {item.rouId})</span>
                                        </span>
                                    </div>
                                    <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${isSelected ? '#EAC024' : '#D1D5DB'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>
                                        {isSelected && <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#EAC024' }} />}
                                    </div>
                                </div>
                            )
                        })}
                    </CollapsibleCard>
                ))
            )}
        </SidePanel>

        <div style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', backgroundColor: 'white', overflowY: 'auto' }}>
            {isLoading ? (
                <div style={{ textAlign: 'center', color: '#9CA3AF', marginTop: '100px' }}>Cargando...</div>
            ) : selectedGenRoute ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'white', borderRadius: '16px', border: '1px solid #D1D5DB', overflow: 'hidden', minHeight: '450px' }}>      
                    <div style={{ padding: '16px 24px', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#1F2937' }}>Comparativa de Trazado</h3>
                            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#6B7280', fontWeight: 500 }}>{selectedGenRoute.cityName} - {selectedGenRoute.routeName} (ID: {selectedGenRoute.rouId})</p>
                        </div>
                        <button onClick={handleOpenRevertModal} style={{ background: '#FFF1F2', color: '#EF4444', border: '1px solid #FECDD3', padding: '8px 14px', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }} onMouseOver={(e) => { e.currentTarget.style.background = '#FEE2E2'; e.currentTarget.style.borderColor = '#FCA5A5'; }} onMouseOut={(e) => { e.currentTarget.style.background = '#FFF1F2'; e.currentTarget.style.borderColor = '#FECDD3'; }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path><path d="M3 3v5h5"></path></svg>
                            Revertir cambios
                        </button>
                    </div>
                    <div style={{ position: 'relative', width: '100%', flex: 1, minHeight: '200px', backgroundColor: '#E5E7EB' }}>
                        <ComparisonMapView key={`${selectedGenRoute.rouId}-${mapUpdateKey}`} data={selectedGenRoute} showOld={showOldRoute} showNew={showNewRoute} />
                    </div>
                    <div style={{ display: 'flex', padding: '16px 20px', background: '#FAFAFA', borderTop: '1px solid #E5E7EB', flexShrink: 0 }}>
                        <div style={{ flex: 1, borderRight: '1px dashed #D1D5DB', paddingRight: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: showOldRoute ? 1 : 0.4, transition: 'opacity 0.2s' }}>
                            <span style={{ display: 'block', fontSize: '12px', color: '#EF4444', textTransform: 'uppercase', fontWeight: 800, marginBottom: '4px' }}>Paradas antes (Ayer)</span>
                            <span style={{ fontSize: '24px', color: '#EF4444', fontWeight: 800 }}>{selectedGenRoute.oldStops}</span>
                            <div onClick={() => setShowOldRoute(!showOldRoute)} style={{ width: 44, height: 24, borderRadius: 24, backgroundColor: showOldRoute ? '#EF4444' : '#D1D5DB', position: 'relative', cursor: 'pointer', transition: 'background-color 0.2s', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)', marginTop: '6px' }}>
                                <div style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: 'white', position: 'absolute', top: 2, left: showOldRoute ? 22 : 2, transition: 'left 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}/>
                            </div>
                        </div>
                        <div style={{ flex: 1, paddingLeft: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: showNewRoute ? 1 : 0.4, transition: 'opacity 0.2s' }}>
                            <span style={{ display: 'block', fontSize: '12px', color: '#3B82F6', textTransform: 'uppercase', fontWeight: 800, marginBottom: '4px' }}>Paradas ahora (Hoy)</span>
                            <span style={{ fontSize: '24px', fontWeight: 900, color: '#3B82F6' }}>{selectedGenRoute.newStops}</span>
                            <div onClick={() => setShowNewRoute(!showNewRoute)} style={{ width: 44, height: 24, borderRadius: 24, backgroundColor: showNewRoute ? '#3B82F6' : '#D1D5DB', position: 'relative', cursor: 'pointer', transition: 'background-color 0.2s', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)', marginTop: '6px' }}>
                                <div style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: 'white', position: 'absolute', top: 2, left: showNewRoute ? 22 : 2, transition: 'left 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)', boxShadow: '0 1px 2px rgba(0,0,0,0.2)' }}/>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ textAlign: 'center', color: '#9CA3AF', marginTop: '100px' }}><span style={{ fontSize: '48px', display: 'block', marginBottom: '16px' }}>✅</span><h2>Todo al día</h2><p>No hay cambios recientes por revisar.</p></div>
            )}
        </div>
    </div>
  );
}